import { prepareMediaFile, prepareMediaFiles } from './mediaPreparation';

/**
 * Typed client for the app's own API.
 *
 * Every call carries the session cookie; nothing here ever handles a Nextcloud
 * credential directly.
 */

export interface User {
  id: string;
  displayName: string;
  /** Changes when the profile photo changes, so avatar URLs can bust caches. */
  avatarVersion?: number;
}

export interface Photo {
  /** Product identity shared by the post's permission-scoped file copies. */
  postId?: string;
  fileId: string;
  name: string;
  path: string;
  contentType: string;
  size: number;
  lastModified: number;
  hasPreview: boolean;
  owner: string;
  /** The author's account id, for their profile photo. Absent on legacy rows. */
  ownerId?: string;
  isVideo: boolean;
  album?: string;
  audience?: { id: string; label: string };
  likeCount?: number;
  likedByMe?: boolean;
  caption?: string;
  /** Drop media can live outside the legacy album root. */
  downloadUrl?: string;
  /** Browser-ready derivative for Drop covers, including HEIC and video. */
  previewUrl?: string;
  mediaId?: string;
  includedInDrop?: boolean;
  sourceMessageId?: number | null;
}

export interface FeedResponse {
  items: Photo[];
  moments: Array<{ kind: 'on_this_day' | 'rediscover'; title: string; detail: string; photos: Photo[] }>;
  nextCursor: string | null;
}

export interface Group {
  id: string;
  label: string;
}

export interface CirclePerson {
  userId: string;
  displayName: string;
}

export interface CircleCandidate extends CirclePerson {
  sharedCircles: Group[];
}

export interface SharingCircle extends Group {
  members: CirclePerson[];
  candidates: CircleCandidate[];
}

export interface StoryAudience extends Group {
  viewed: boolean;
  reaction: string | null;
}

export interface FamilyStory {
  id: string;
  authorId: string;
  authorName: string;
  name: string;
  contentType: string;
  size: number;
  caption: string | null;
  createdAt: number;
  expiresAt: number;
  active: boolean;
  isVideo: boolean;
  isMine: boolean;
  previewUrl: string;
  mediaUrl: string;
  audiences: StoryAudience[];
}

export interface StoryThread {
  reactions: Array<{ reaction: string; count: number; reacted: boolean }>;
  replies: Array<{
    id: string;
    userId: string;
    displayName: string;
    message: string;
    createdAt: number;
    isOwn: boolean;
  }>;
  seenBy: Array<{ userId: string; displayName: string; viewedAt: number }>;
}

export interface StoryDetail {
  story: FamilyStory;
  selectedAudienceId: string;
  thread: StoryThread;
}

export interface FamilyInviteLink {
  id: string;
  group: Group;
  createdAt: number;
  expiresAt: number;
  maxUses: number;
  useCount: number;
  remainingUses: number;
  revoked: boolean;
  available: boolean;
}

export interface Like {
  userId: string;
  displayName: string;
  createdAt: number;
}

export interface Album {
  name: string;
  path: string;
  fileId: string;
  lastModified: number;
}

export interface Comment {
  id: string;
  message: string;
  actorId: string;
  actorDisplayName: string;
  createdAt: number;
}

export interface ChatRoom {
  token: string;
  name: string;
  type: number;
  unread: number;
  unreadMention: boolean;
  lastActivity: number;
  preview: string;
}

export interface ChatMessage {
  id: number;
  referenceId?: string;
  actorId: string;
  actorDisplayName: string;
  message: string;
  timestamp: number;
  isOwn: boolean;
  isReplyable: boolean;
  reactions: ChatReaction[];
  parent: ChatMessageParent | null;
  attachment: ChatAttachment | null;
  /** Client-only state for an outgoing message awaiting Talk confirmation. */
  deliveryState?: 'sending' | 'failed';
}

export interface ChatAttachment {
  fileId: string;
  name: string;
  mimeType: string;
  size: number;
  previewAvailable: boolean;
}

export interface ChatMessageParent {
  id: number;
  actorId: string;
  actorDisplayName: string;
  message: string;
  deleted: boolean;
  isOwn: boolean;
}

export interface ChatReaction {
  reaction: string;
  count: number;
  reacted: boolean;
}

export interface ChatMention {
  id: string;
  label: string;
  mentionId: string;
  source: string;
}

export interface ChatPerson {
  userId: string;
  displayName: string;
}

export interface FamilyProfile {
  userId: string;
  displayName: string;
  email: string;
  phone: string;
  address: string;
  biography: string;
  headline: string;
  role: string;
  organisation: string;
  pronouns: string;
}

/** The fields a person may edit; every one is optional on save. */
export type FamilyProfileEdit = Partial<Omit<FamilyProfile, 'userId'>>;

export interface ChatParticipant {
  userId: string;
  displayName: string;
  isModerator: boolean;
  isSelf: boolean;
}

export type DropNotificationMode = 'all' | 'photos_mentions' | 'mentions';

export interface DropNotificationPreference {
  mode: DropNotificationMode;
  mutedUntil: number | null;
}

export interface FamilyDropParticipant {
  userId: string;
  displayName: string;
}

export interface FamilyDrop {
  id: string;
  title: string;
  createdBy: string;
  talkToken: string | null;
  photoAudience: Group;
  photoAudienceCount: number;
  audienceWiderThanConversation: boolean;
  startsAt: number | null;
  endsAt: number | null;
  createdAt: number;
  updatedAt: number;
  participants: FamilyDropParticipant[];
  canChat: boolean;
  canViewPhotos: boolean;
  canAddPhotos: boolean;
  notificationPreference: DropNotificationPreference;
  photos: Photo[];
  photoCount: number;
  coverFileId: string | null;
  showPrivacyNotice: boolean;
}

export interface DropMember extends FamilyDropParticipant {
  inPhotos: boolean;
  inConversation: boolean;
  isSelf: boolean;
}

export interface DropMembership {
  photoAudience: Group;
  photoMembers: DropMember[];
  photoMemberCount: number;
  photoMembersAvailable: boolean;
  conversationMembers: DropMember[];
  people: DropMember[];
  canAddPhotoMembers: boolean;
  canAddConversationMembers: boolean;
}

export interface DropSetupAudience extends Group {
  memberIds: string[];
}

export interface DropSetup {
  audiences: DropSetupAudience[];
  people: ChatPerson[];
}

export interface DropChatMediaState {
  mediaId: string;
  messageId: number;
  included: boolean;
}

export interface GuestDropManagement {
  drop: {
    id: string; title: string; status: string; guestAccessEndsAt: number | null;
    delegatedPhotoInvites: boolean; delegatedUploadInvites: boolean; uploadPaused: boolean;
  };
  guests: Array<{
    id: string; display_name: string; local_label: string | null; status: string;
    created_at: number; last_seen_at: number; view_media: number; upload_media: number;
    read_conversation: number; send_messages: number; create_photo_invites: number;
    conversation_visible_from: number | null;
  }>;
  invites: Array<{
    id: string; view_media: number; upload_media: number; read_conversation: number;
    send_messages: number; history_mode: 'from_now' | 'entire'; max_uses: number;
    use_count: number; expires_at: number; revoked_at: number | null; has_pin: number;
  }>;
  promotions: Array<{
    id: string; guest_id: string; expected_member_id: string;
    state: 'requested' | 'linked' | 'cancelled' | 'failed';
    expires_at: number; created_at: number; completed_at: number | null;
  }>;
}

export interface ExternalDropMedia {
  id: string; name: string; contentType: string | null; size: number; owner: string;
  state: string; createdAt: number; previewUrl: string | null; downloadUrl: string | null;
}
export interface ExternalDropMessage {
  id: number; actorId: string; actorDisplayName: string; actorType: string;
  message: string; timestamp: number; isOwn: boolean; removed: boolean;
}

export type ChatPresenceBootstrap =
  | {
      available: true;
      typingEnabled: true;
      sessionId: string;
      userId: string;
      backendUrl: string;
      helloAuthToken: string;
    }
  | {
      available: false;
      typingEnabled: false;
      reason: 'privacy_disabled' | 'unsupported';
    };

export interface PushDevice {
  id: string;
  platform: string;
  userAgent: string;
  createdAt: number;
  lastSuccessAt: number | null;
  lastError: string | null;
  failureCount: number;
  healthy: boolean;
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    credentials: 'include',
    ...init,
    headers: {
      Accept: 'application/json',
      ...(init?.body && !(init.body instanceof FormData)
        ? { 'Content-Type': 'application/json' }
        : {}),
      ...init?.headers,
    },
  });

  if (!response.ok) {
    if (response.status === 401) {
      window.dispatchEvent(new Event('family:auth-invalidated'));
    }
    let code: string | undefined;
    let message = `Request failed (${response.status})`;

    try {
      const body = (await response.json()) as { error?: string; message?: string };
      code = body.error;
      if (body.message) message = body.message;
      else if (body.error) message = humanizeError(body.error);
    } catch {
      // Non-JSON error body; the generic message stands.
    }

    throw new ApiError(message, response.status, code);
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

async function requestBlob(path: string, signal?: AbortSignal): Promise<Blob> {
  const response = await fetch(path, {
    credentials: 'include',
    headers: { Accept: 'image/avif,image/webp,image/*' },
    signal,
  });
  if (response.status === 401) {
    window.dispatchEvent(new Event('family:auth-invalidated'));
  }
  if (!response.ok) {
    throw new ApiError(`Preview could not be loaded (${response.status})`, response.status);
  }
  const blob = await response.blob();
  if (!blob.type.startsWith('image/')) {
    throw new ApiError('The server did not return an image preview.', 502);
  }
  return blob;
}

function humanizeError(code: string): string {
  switch (code) {
    case 'not_authenticated':
      return 'You are signed out. Please sign in again.';
    case 'nextcloud_unreachable':
      return 'Could not reach the family server. Check your connection and try again.';
    case 'nextcloud_auth_failed':
      return 'Your access expired. Please sign in again.';
    case 'nextcloud_busy':
      return 'The family cloud is catching up. Please try again in a minute.';
    case 'not_found':
      return 'That is not there any more.';
    case 'no_subscriptions':
      return 'This device has not been set up for notifications yet.';
    default:
      return 'Something went wrong. Please try again.';
  }
}

export const api = {
  // --- auth ---------------------------------------------------------------

  me: () =>
    request<{ authenticated: boolean; user?: User }>('/api/auth/me'),

  startLogin: () =>
    request<{ state: string; loginUrl: string }>('/api/auth/start', { method: 'POST' }),

  pollLogin: (state: string) =>
    request<{ status: 'pending' | 'complete'; user?: User }>('/api/auth/poll', {
      method: 'POST',
      body: JSON.stringify({ state }),
    }),

  logout: () => request<{ ok: true }>('/api/auth/logout', { method: 'POST' }),

  // --- photos -------------------------------------------------------------

  config: () =>
    request<{ nextcloudUrl: string; photosPath: string; familyGroup: string }>(
      '/api/config',
    ),

  version: () => request<{ version: string }>('/api/version', { cache: 'no-store' }),

  feed: (cursor?: string | null, view: 'family' | 'mine' = 'family') => {
    const query = new URLSearchParams({ view });
    if (cursor) query.set('cursor', cursor);
    return request<FeedResponse>(`/api/feed?${query}`);
  },

  feedPost: (postId: string) =>
    request<{ post: Photo }>(`/api/feed/post/${encodeURIComponent(postId)}`),

  groups: () => request<{ groups: Group[] }>('/api/groups'),

  sharingCircles: () =>
    request<{ circles: SharingCircle[]; canAddMembers: boolean }>('/api/circles'),

  addCircleMember: (circleId: string, userId: string) =>
    request<{ ok: true; added: boolean; person: CirclePerson }>(
      `/api/circles/${encodeURIComponent(circleId)}/members`,
      { method: 'POST', body: JSON.stringify({ userId }) },
    ),

  albums: () => request<{ albums: Album[] }>('/api/albums'),

  createAlbum: (name: string) =>
    request<{ ok: true; name: string }>('/api/albums', {
      method: 'POST',
      body: JSON.stringify({ name }),
    }),

  albumPhotos: (album: string) =>
    request<{ photos: Photo[] }>(`/api/albums/${encodeURIComponent(album)}/photos`),

  comments: (fileId: string) =>
    request<{ comments: Comment[] }>(`/api/photos/${fileId}/comments`),

  addComment: (fileId: string, message: string) =>
    request<{ ok: true }>(`/api/photos/${fileId}/comments`, {
      method: 'POST',
      body: JSON.stringify({ message }),
    }),

  like: (fileId: string) =>
    request<{ likeCount: number; likedByMe: boolean }>(`/api/photos/${fileId}/like`, {
      method: 'POST',
    }),

  unlike: (fileId: string) =>
    request<{ likeCount: number; likedByMe: boolean }>(`/api/photos/${fileId}/like`, {
      method: 'DELETE',
    }),

  likes: (fileId: string) =>
    request<{ likeCount: number; likedByMe: boolean; likes: Like[] }>(
      `/api/photos/${fileId}/likes`,
    ),

  /** Upload with progress, which fetch cannot report — hence XHR. */
  upload: async (
    files: File[],
    groups: string[],
    caption: string,
    onProgress?: (fraction: number) => void,
  ): Promise<{ uploaded: Array<{ name: string; fileId: string; postId?: string; groupId?: string }>; rejected: Array<{ name: string; reason: string }> }> => {
    const preparedFiles = await prepareMediaFiles(files, (fraction) => onProgress?.(fraction * 0.3));
    const form = new FormData();
    for (const file of preparedFiles) form.append('files', file, file.name);

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      const query = new URLSearchParams({ groups: groups.join(',') });
      const trimmedCaption = caption.trim();
      if (trimmedCaption) query.set('caption', trimmedCaption);
      xhr.open('POST', `/api/upload?${query}`);
      xhr.withCredentials = true;

      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable && onProgress) {
          onProgress(0.3 + (event.loaded / event.total) * 0.7);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            resolve(JSON.parse(xhr.responseText));
          } catch {
            reject(new ApiError('Upload finished but the reply was unreadable.', xhr.status));
          }
        } else {
          reject(new ApiError(`Upload failed (${xhr.status})`, xhr.status));
        }
      });

      xhr.addEventListener('error', () =>
        reject(new ApiError('Upload failed. Check your connection.', 0)),
      );
      xhr.addEventListener('abort', () => reject(new ApiError('Upload cancelled.', 0)));

      xhr.send(form);
    });
  },

  uploadToAlbum: async (
    files: File[],
    album: string,
    onProgress?: (fraction: number) => void,
  ): Promise<{ uploaded: Array<{ name: string; fileId: string }>; rejected: Array<{ name: string; reason: string }> }> => {
    const preparedFiles = await prepareMediaFiles(files, (fraction) => onProgress?.(fraction * 0.3));
    const form = new FormData();
    for (const file of preparedFiles) form.append('files', file, file.name);
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `/api/upload?album=${encodeURIComponent(album)}`);
      xhr.withCredentials = true;
      xhr.upload.addEventListener('progress', (event) => { if (event.lengthComputable && onProgress) onProgress(0.3 + (event.loaded / event.total) * 0.7); });
      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try { resolve(JSON.parse(xhr.responseText)); } catch { reject(new ApiError('Upload finished but the reply was unreadable.', xhr.status)); }
        } else reject(new ApiError(`Upload failed (${xhr.status})`, xhr.status));
      });
      xhr.addEventListener('error', () => reject(new ApiError('Upload failed. Check your connection.', 0)));
      xhr.send(form);
    });
  },

  // --- Family Stories -----------------------------------------------------

  storySetup: () => request<{ audiences: Group[] }>('/api/stories/setup'),

  stories: (scope: 'active' | 'archive' = 'active') =>
    request<{ stories: FamilyStory[] }>(`/api/stories?scope=${scope}`, { cache: 'no-store' }),

  story: (id: string, audienceId?: string) => {
    const query = audienceId ? `?audience=${encodeURIComponent(audienceId)}` : '';
    return request<StoryDetail>(`/api/stories/${encodeURIComponent(id)}${query}`, { cache: 'no-store' });
  },

  uploadStory: async (
    file: File,
    audiences: string[],
    caption: string,
    onProgress?: (fraction: number) => void,
  ): Promise<{ story: FamilyStory }> => {
    const preparedFile = await prepareMediaFile(file, (fraction) => onProgress?.(fraction * 0.3));
    const form = new FormData();
    form.append('file', preparedFile, preparedFile.name);
    const query = new URLSearchParams({ audiences: audiences.join(',') });
    if (caption.trim()) query.set('caption', caption.trim());
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `/api/stories?${query}`);
      xhr.withCredentials = true;
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable && onProgress) onProgress(0.3 + (event.loaded / event.total) * 0.7);
      });
      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try { resolve(JSON.parse(xhr.responseText)); } catch { reject(new ApiError('Story finished uploading, but the reply was unreadable.', xhr.status)); }
          return;
        }
        try {
          const body = JSON.parse(xhr.responseText) as { error?: string; message?: string };
          reject(new ApiError(body.message ?? humanizeError(body.error ?? 'story_upload_failed'), xhr.status, body.error));
        } catch {
          reject(new ApiError(`Story upload failed (${xhr.status})`, xhr.status));
        }
      });
      xhr.addEventListener('error', () => reject(new ApiError('Story upload failed. Check your connection.', 0)));
      xhr.addEventListener('abort', () => reject(new ApiError('Story upload cancelled.', 0)));
      xhr.send(form);
    });
  },

  markStoryViewed: (id: string, audienceId: string) =>
    request<{ ok: true }>(`/api/stories/${encodeURIComponent(id)}/view`, {
      method: 'POST',
      body: JSON.stringify({ audienceId }),
    }),

  setStoryReaction: (id: string, audienceId: string, reaction: string | null) =>
    request<{ thread: StoryThread }>(`/api/stories/${encodeURIComponent(id)}/reaction`, {
      method: 'PUT',
      body: JSON.stringify({ audienceId, reaction }),
    }),

  replyToStory: (id: string, audienceId: string, message: string) =>
    request<{ thread: StoryThread }>(`/api/stories/${encodeURIComponent(id)}/replies`, {
      method: 'POST',
      body: JSON.stringify({ audienceId, message }),
    }),

  // --- Family Drops -------------------------------------------------------

  dropSetup: () => request<DropSetup>('/api/drops/setup'),

  drops: () => request<{ drops: FamilyDrop[] }>('/api/drops'),

  drop: (id: string) => request<{ drop: FamilyDrop }>(
    `/api/drops/${encodeURIComponent(id)}`,
    { cache: 'no-store' },
  ),

  acknowledgeDropPrivacyNotice: (id: string) => request<{ ok: true }>(
    `/api/drops/${encodeURIComponent(id)}/privacy-notice/seen`,
    { method: 'POST' },
  ),

  dropMembership: (id: string) => request<{ membership: DropMembership }>(
    `/api/drops/${encodeURIComponent(id)}/members`,
    { cache: 'no-store' },
  ),

  addDropMember: (id: string, input: {
    userId: string;
    addToPhotos: boolean;
    addToConversation: boolean;
  }) => request<{ ok: true; photoAdded: boolean; conversationAdded: boolean }>(
    `/api/drops/${encodeURIComponent(id)}/members`,
    { method: 'POST', body: JSON.stringify(input) },
  ),

  dropForRoom: (token: string) => request<{
    drop: FamilyDrop;
    mediaMessages: DropChatMediaState[];
  }>(`/api/drops/by-room/${encodeURIComponent(token)}`, { cache: 'no-store' }),

  createDrop: (input: {
    title: string;
    conversationUserIds: string[];
    photoAudienceId: string;
    startsAt: number | null;
    endsAt: number | null;
  }) => request<{ drop: FamilyDrop; failedParticipants: string[] }>('/api/drops', {
    method: 'POST',
    body: JSON.stringify(input),
  }),

  uploadDropPhotos: async (
    id: string,
    files: File[],
    onProgress?: (fraction: number) => void,
  ): Promise<{ uploaded: Photo[]; rejected: Array<{ name: string; reason: string }> }> => {
    const preparedFiles = await prepareMediaFiles(files, (fraction) => onProgress?.(fraction * 0.3));
    const form = new FormData();
    for (const file of preparedFiles) form.append('files', file, file.name);
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `/api/drops/${encodeURIComponent(id)}/photos`);
      xhr.withCredentials = true;
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable && onProgress) onProgress(0.3 + (event.loaded / event.total) * 0.7);
      });
      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try { resolve(JSON.parse(xhr.responseText)); } catch { reject(new ApiError('Upload finished but the reply was unreadable.', xhr.status)); }
          return;
        }
        try {
          const body = JSON.parse(xhr.responseText) as { error?: string; message?: string };
          reject(new ApiError(body.message ?? humanizeError(body.error ?? 'upload_failed'), xhr.status, body.error));
        } catch {
          reject(new ApiError(`Upload failed (${xhr.status})`, xhr.status));
        }
      });
      xhr.addEventListener('error', () => reject(new ApiError('Upload failed. Check your connection.', 0)));
      xhr.send(form);
    });
  },

  setDropNotifications: (
    id: string,
    preference: DropNotificationPreference,
  ) => request<{ preference: DropNotificationPreference }>(
    `/api/drops/${encodeURIComponent(id)}/notifications`,
    { method: 'PUT', body: JSON.stringify(preference) },
  ),

  setDropChatMedia: (
    id: string,
    messageId: number,
    fileId: string,
    included: boolean,
  ) => request<{ mediaId: string | null; included: boolean }>(
    `/api/drops/${encodeURIComponent(id)}/chat-media/${messageId}`,
    { method: 'PUT', body: JSON.stringify({ fileId, included }) },
  ),

  guestDropCapabilities: () => request<{ enabled: boolean }>('/api/guest-drops/capabilities'),

  enableGuestSharing: (id: string) => request<{ ok: true; status: string }>(
    `/api/drops/${encodeURIComponent(id)}/guest-sharing`, { method: 'POST' },
  ),

  guestSharing: (id: string) => request<{
    enabled: boolean; status?: string; management?: GuestDropManagement;
  }>(`/api/drops/${encodeURIComponent(id)}/guest-sharing`, { cache: 'no-store' }),

  createGuestInvite: (id: string, input: {
    access: { viewMedia: true; uploadMedia: boolean; readConversation: boolean; sendMessages: boolean; historyMode: 'from_now' | 'entire' };
    maxUses: number; expiresAt: number; pin?: string;
  }) => request<{ link: string; invite: unknown }>(`/api/drops/${encodeURIComponent(id)}/guest-invites`, {
    method: 'POST', body: JSON.stringify(input),
  }),

  revokeGuestInvite: (id: string, inviteId: string) => request<{ ok: true }>(
    `/api/drops/${encodeURIComponent(id)}/guest-invites/${encodeURIComponent(inviteId)}`, { method: 'DELETE' },
  ),

  updateGuestAccess: (id: string, guestId: string, access: Record<string, boolean | number | null>) => request<{ grant: unknown }>(
    `/api/drops/${encodeURIComponent(id)}/guests/${encodeURIComponent(guestId)}`, { method: 'PATCH', body: JSON.stringify(access) },
  ),

  revokeGuest: (id: string, guestId: string) => request<{ ok: true }>(
    `/api/drops/${encodeURIComponent(id)}/guests/${encodeURIComponent(guestId)}`, { method: 'DELETE' },
  ),

  promoteGuest: (id: string, guestId: string, memberId: string) => request<{
    ok: true; state: 'requested'; promotionId: string; memberId: string; expiresAt: number;
  }>(
    `/api/drops/${encodeURIComponent(id)}/guests/${encodeURIComponent(guestId)}/promotion`,
    { method: 'POST', body: JSON.stringify({ memberId }) },
  ),

  externalDrop: (id: string) => request<{ drop: {
    id: string; title: string; grant: { readConversation: boolean; sendMessages: boolean };
    media: ExternalDropMedia[];
    pendingPromotions: Array<{ id: string; guestId: string; guestDisplayName: string; expiresAt: number }>;
  } }>(`/api/external-drops/${encodeURIComponent(id)}`, { cache: 'no-store' }),

  confirmGuestPromotion: (id: string, promotionId: string) => request<{ ok: true; state: 'linked'; memberId: string }>(
    `/api/external-drops/${encodeURIComponent(id)}/promotions/${encodeURIComponent(promotionId)}/confirm`,
    { method: 'POST' },
  ),

  externalDropMessages: (id: string) => request<{ messages: ExternalDropMessage[] }>(
    `/api/external-drops/${encodeURIComponent(id)}/messages`, { cache: 'no-store' },
  ),

  sendExternalDropMessage: (id: string, body: string) => request<{ messages: ExternalDropMessage[] }>(
    `/api/external-drops/${encodeURIComponent(id)}/messages`, { method: 'POST', body: JSON.stringify({ body }) },
  ),

  // --- push ---------------------------------------------------------------

  vapidKey: () => request<{ publicKey: string }>('/api/push/vapid-key'),

  subscribePush: (subscription: PushSubscriptionJSON, platform: string) =>
    request<{ ok: true; subscriptionId: string }>('/api/push/subscribe', {
      method: 'POST',
      body: JSON.stringify({
        endpoint: subscription.endpoint,
        keys: subscription.keys,
        platform,
      }),
    }),

  unsubscribePush: (endpoint: string) =>
    request<{ ok: true }>('/api/push/unsubscribe', {
      method: 'POST',
      body: JSON.stringify({ endpoint }),
    }),

  pushStatus: () => request<{ devices: PushDevice[] }>('/api/push/status'),

  testPush: (subscriptionId?: string) =>
    request<{ results: Array<{ subscriptionId: string; ok: boolean; error?: string }> }>(
      '/api/push/test',
      { method: 'POST', body: JSON.stringify({ subscriptionId }) },
    ),

  // --- chat (Nextcloud Talk) ----------------------------------------------

  chatRooms: () => request<{ rooms: ChatRoom[] }>('/api/chat/rooms'),

  chatPeople: () => request<{ people: ChatPerson[] }>('/api/chat/people'),

  chatSettings: () => request<{ supported: boolean; enabled: boolean }>(
    '/api/chat/settings',
    { cache: 'no-store' },
  ),

  setChatTypingPreference: (enabled: boolean) =>
    request<{ supported: boolean; enabled: boolean }>('/api/chat/settings/typing', {
      method: 'PUT',
      body: JSON.stringify({ enabled }),
    }),

  startChatPresence: (token: string) => request<ChatPresenceBootstrap>(
    `/api/chat/rooms/${encodeURIComponent(token)}/presence`,
    { method: 'POST', cache: 'no-store' },
  ),

  stopChatPresence: (token: string) => request<void>(
    `/api/chat/rooms/${encodeURIComponent(token)}/presence`,
    { method: 'DELETE', keepalive: true },
  ),

  chatMessages: (
    token: string,
    options: {
      lastKnownMessageId?: number;
      lastCommonReadId?: number;
      waitForNew?: boolean;
      signal?: AbortSignal;
    } = {},
  ) => {
    const query = new URLSearchParams();
    if (options.lastKnownMessageId !== undefined) {
      query.set('lastKnownMessageId', String(options.lastKnownMessageId));
    }
    if (options.lastCommonReadId !== undefined) {
      query.set('lastCommonReadId', String(options.lastCommonReadId));
    }
    if (options.waitForNew) query.set('waitForNew', '1');

    const suffix = query.size > 0 ? `?${query}` : '';
    return request<{
      messages: ChatMessage[];
      lastReadMessage: number | null;
      lastCommonReadMessage: number | null;
    }>(
      `/api/chat/rooms/${encodeURIComponent(token)}/messages${suffix}`,
      { cache: 'no-store', signal: options.signal },
    );
  },

  sendChatMessage: (token: string, message: string, referenceId: string, replyTo?: number) =>
    request<{ message: ChatMessage }>(
      `/api/chat/rooms/${encodeURIComponent(token)}/messages`,
      { method: 'POST', body: JSON.stringify({ message, referenceId, replyTo }) },
    ),

  chatMentions: (token: string, search: string) =>
    request<{ mentions: ChatMention[] }>(
      `/api/chat/rooms/${encodeURIComponent(token)}/mentions?search=${encodeURIComponent(search)}`,
    ),

  chatAttachmentPreview: (
    token: string,
    messageId: number,
    fileId: string,
    size = 640,
    signal?: AbortSignal,
  ) => requestBlob(
    `/api/chat/rooms/${encodeURIComponent(token)}/messages/${messageId}/attachments/${encodeURIComponent(fileId)}?preview=1&size=${size}`,
    signal,
  ),

  setChatReaction: (token: string, messageId: number, reaction: string, remove = false) =>
    request<{ reactions: ChatReaction[] }>(
      `/api/chat/rooms/${encodeURIComponent(token)}/messages/${messageId}/reactions`,
      { method: remove ? 'DELETE' : 'POST', body: JSON.stringify({ reaction }) },
    ),

  uploadChatAttachment: async (
    token: string,
    file: File,
    replyTo?: number,
    includeInDrop?: boolean,
    onProgress?: (fraction: number) => void,
  ): Promise<{
    message: ChatMessage | null;
    dropMedia: { mediaId: string; messageId: number | null; included: true } | null;
  }> => {
    const preparedFile = await prepareMediaFile(file, (fraction) => onProgress?.(fraction * 0.3));
    if (preparedFile.size > 100 * 1024 * 1024) {
      throw new ApiError('That video is still larger than 100 MB after optimization. Try a shorter clip.', 413, 'attachment_too_large');
    }
    const form = new FormData();
    form.append('file', preparedFile, preparedFile.name);
    const queryParams = new URLSearchParams();
    if (replyTo) queryParams.set('replyTo', String(replyTo));
    if (includeInDrop !== undefined) queryParams.set('includeInDrop', includeInDrop ? '1' : '0');
    const query = queryParams.size > 0 ? `?${queryParams}` : '';

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `/api/chat/rooms/${encodeURIComponent(token)}/attachments${query}`);
      xhr.withCredentials = true;
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable && onProgress) onProgress(0.3 + (event.loaded / event.total) * 0.7);
      });
      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            resolve(JSON.parse(xhr.responseText));
          } catch {
            reject(new ApiError('Attachment finished uploading, but the reply was unreadable.', xhr.status));
          }
          return;
        }

        try {
          const body = JSON.parse(xhr.responseText) as { error?: string; message?: string };
          reject(new ApiError(body.message ?? humanizeError(body.error ?? 'upload_failed'), xhr.status, body.error));
        } catch {
          reject(new ApiError(`Attachment failed (${xhr.status})`, xhr.status));
        }
      });
      xhr.addEventListener('error', () => reject(new ApiError('Attachment failed. Check your connection.', 0)));
      xhr.addEventListener('abort', () => reject(new ApiError('Attachment upload cancelled.', 0)));
      xhr.send(form);
    });
  },

  markChatRead: (token: string, lastReadMessage: number) =>
    request<{ ok: true }>(`/api/chat/rooms/${encodeURIComponent(token)}/read`, {
      method: 'POST',
      body: JSON.stringify({ lastReadMessage }),
    }),

  chatReadState: (token: string) => request<{ lastCommonReadMessage: number }>(
    `/api/chat/rooms/${encodeURIComponent(token)}/read-state`,
    { cache: 'no-store' },
  ),

  markChatActive: (token: string, clientId: string) => request<{ ok: true }>(
    `/api/chat/rooms/${encodeURIComponent(token)}/active?clientId=${encodeURIComponent(clientId)}`,
    { method: 'POST', cache: 'no-store' },
  ),

  clearChatActive: (token: string, clientId: string) => request<void>(
    `/api/chat/rooms/${encodeURIComponent(token)}/active?clientId=${encodeURIComponent(clientId)}`,
    { method: 'DELETE', keepalive: true },
  ),

  createChatRoom: (name: string, participants: string[]) =>
    request<{ token: string; failedParticipants: string[] }>('/api/chat/rooms', {
      method: 'POST',
      body: JSON.stringify({ name, participants }),
    }),

  chatParticipants: (token: string) =>
    request<{ participants: ChatParticipant[]; canAddParticipants: boolean }>(
      `/api/chat/rooms/${encodeURIComponent(token)}/participants`,
    ),

  addChatParticipant: (token: string, userId: string) =>
    request<{ ok: true; alreadyPresent: boolean }>(
      `/api/chat/rooms/${encodeURIComponent(token)}/participants`,
      {
        method: 'POST',
        body: JSON.stringify({ userId }),
      },
    ),

  // --- profile -------------------------------------------------------------

  profile: () => request<{ profile: FamilyProfile }>('/api/profile'),

  saveProfile: (edits: FamilyProfileEdit) =>
    request<{ profile: FamilyProfile; fieldErrors: Record<string, string> | null }>(
      '/api/profile',
      { method: 'PUT', body: JSON.stringify(edits) },
    ),

  /**
   * Avatars are served by this app rather than Nextcloud: the browser has no
   * Nextcloud credential, only the session cookie. The version parameter is
   * what makes a newly saved photo replace the cached one in <img> tags.
   */
  avatarUrl: (userId: string, size = 64, version?: number) => {
    const suffix = version ? `&v=${version}` : '';
    return `/api/profile/avatar/${encodeURIComponent(userId)}?size=${size}${suffix}`;
  },

  uploadAvatar: async (file: File) => {
    const body = new FormData();
    body.append('file', file);
    const response = await fetch('/api/profile/avatar', {
      method: 'POST',
      body,
      credentials: 'same-origin',
    });
    if (!response.ok) {
      const detail = await response.json().catch(() => null) as { message?: string } | null;
      throw new Error(detail?.message ?? 'That photo could not be saved.');
    }
    return response.json() as Promise<{ ok: true; avatarVersion: number }>;
  },

  removeAvatar: () =>
    request<{ ok: true; avatarVersion: number }>('/api/profile/avatar', { method: 'DELETE' }),

  // --- admin ---------------------------------------------------------------

  adminStatus: () =>
    request<{ isAdmin: boolean; familyGroup: string; groups: Group[] }>('/api/admin/status'),

  familyMembers: () => request<{ members: string[] }>('/api/admin/members'),

  setupGroups: () => request<{ ok: true; groups: Group[] }>('/api/admin/groups/setup', { method: 'POST' }),

  groupMembers: (groupId: string) =>
    request<{ group: Group; members: string[] }>(`/api/admin/groups/${encodeURIComponent(groupId)}/members`),

  addGroupMember: (groupId: string, userId: string) =>
    request<{ ok: true }>(`/api/admin/groups/${encodeURIComponent(groupId)}/members`, {
      method: 'POST', body: JSON.stringify({ userId }),
    }),

  removeGroupMember: (groupId: string, userId: string) =>
    request<{ ok: true }>(`/api/admin/groups/${encodeURIComponent(groupId)}/members/${encodeURIComponent(userId)}`, { method: 'DELETE' }),

  invite: (userId: string, displayName: string, email: string, groups: string[]) =>
    request<{ ok: true; userId: string; message: string }>('/api/admin/invite', {
      method: 'POST',
      body: JSON.stringify({ userId, displayName, email, groups }),
    }),

  inviteLinks: () => request<{ links: FamilyInviteLink[] }>('/api/admin/invite-links'),

  createInviteLink: (group: string, maxUses: number, expiresInDays: number) =>
    request<{ link: string; invite: FamilyInviteLink }>('/api/admin/invite-links', {
      method: 'POST',
      body: JSON.stringify({ group, maxUses, expiresInDays }),
    }),

  revokeInviteLink: (id: string) => request<{ ok: true }>(`/api/admin/invite-links/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  inspectInviteLink: (token: string) => request<{ invite: FamilyInviteLink }>('/api/join/inspect', {
    method: 'POST',
    body: JSON.stringify({ token }),
  }),

  redeemInviteLink: (token: string, userId: string, displayName: string, email: string) =>
    request<{ ok: true; userId: string; group: Group; message: string }>('/api/join/redeem', {
      method: 'POST',
      body: JSON.stringify({ token, userId, displayName, email }),
    }),
};

/** Thumbnail URL for a photo, served through our own authenticated proxy. */
export function thumbUrl(fileId: string, size = 512): string {
  // Version 2 bypasses old, unpurgeable browser HTTP-cache entries. The
  // service worker owns the bounded, logout-purged preview cache now.
  return `/api/thumb/${fileId}?size=${size}&cache=2`;
}

/**
 * Preview URL for media that may live in Nextcloud or the isolated Guest Drop
 * store. Guest media ids are intentionally not valid Nextcloud file ids, so
 * callers must honor the scoped preview URL supplied by the Drop API.
 */
export function photoPreviewUrl(photo: Photo, size = 512): string {
  if (!photo.previewUrl) return thumbUrl(photo.fileId, size);
  const separator = photo.previewUrl.includes('?') ? '&' : '?';
  return `${photo.previewUrl}${separator}size=${size}`;
}

/** Full-resolution URL for a photo. */
export function originalUrl(path: string): string {
  return `/api/file/${path.split('/').map(encodeURIComponent).join('/')}?cache=2`;
}

/** Memories timeline results intentionally omit paths; this still downloads safely by id. */
export function photoUrl(photo: Photo): string {
  return photo.downloadUrl
    ? photo.downloadUrl
    : photo.path
    ? originalUrl(photo.path)
    : `/api/file-id/${encodeURIComponent(photo.fileId)}?cache=2`;
}
